import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-down.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@spectrum-web-components/popover/sp-popover.js';
import '@spectrum-web-components/sidenav/sp-sidenav.js';
import '@spectrum-web-components/sidenav/sp-sidenav-item.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-send.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-attach.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-microphone.js';
import '@spectrum-web-components/split-view/sp-split-view.js';

import '../../components/confirmation-dialog';
import { api } from '../../api/index.js';
import '../custom-models';
import { assistantMessageStyles } from './components/messages/assistant-message.js';
import './components/messages/user-message.js';
import type { McpServerConfig } from '../../api/mcp-servers.js';
import {
  getBaseApiUrl,
  getStreamApiUrl,
  isProduction,
} from '../../config/environment.js';
import { sharedUiStyles } from '../../styles/shared-ui.styles';
import {
  isToolCallTrace,
  upsertToolCallTrace,
} from './components/messages/tool-call-utils.js';
import type {
  ChatToolCallTrace,
  ConversationAttachment,
  ConversationMessage,
} from './components/messages/types.js';
import {
  type ChatToolSelection,
  EMPTY_CHAT_TOOL_SELECTION,
  getAvailableMcpServers,
  getSelectedToolSourceCount,
  normalizeChatToolSelection,
  persistChatToolSelection,
} from './tool-selection.js';

interface PendingAttachment {
  id: string;
  file: File;
}

interface ChatModelOption {
  id: string;
  label: string;
  supportsTemperature?: boolean;
}

interface CustomModelOption {
  id: string;
  label: string;
  baseModel: string;
  baseModelLabel?: string;
  versionLabel?: string;
  menuLabel?: string;
  supportsTemperature?: boolean;
}

interface ChatModelsConfig {
  defaultModel: string;
  models: ChatModelOption[];
  customModels?: CustomModelOption[];
}

const FALLBACK_DEFAULT_MODEL = 'gpt-5.5';
type ChatView = 'conversation' | 'models';

interface Conversation {
  id: string;
  title: string;
  source: 'static' | 'database';
  messages: ConversationMessage[];
  createdAt?: string;
  updatedAt?: string;
  customModelId?: string | null;
  toolSelection: ChatToolSelection;
}

interface ApiConversation {
  id: string;
  title: string;
  customModelId?: string | null;
  toolSelection?: ChatToolSelection;
  createdAt: string;
  updatedAt: string;
  messages: Array<{
    id: number;
    role: 'user' | 'assistant' | 'system';
    content: string;
    model?: string;
    createdAt: string;
    attachments?: ConversationAttachment[];
    toolCalls?: ChatToolCallTrace[];
  }>;
}

/** Minimum voice recording length in seconds; shorter recordings are not sent (matches server check). */
const MIN_AUDIO_DURATION_SECONDS = 1;

type StaticConversationText = string | (() => string);
type StaticConversationMessage = Omit<ConversationMessage, 'content'> & {
  content: StaticConversationText;
};

const STATIC_CONVERSATION_SEED: Array<{
  id: string;
  title: StaticConversationText;
  messages: StaticConversationMessage[];
}> = [
  {
    id: 'tax-feature',
    title: () => msg('Tax feature Q&A'),
    messages: [
      {
        role: 'user',
        content: () =>
          msg(
            "How does Klarvos' tax feature calculate deductions for freelance income?"
          ),
      },
      {
        role: 'assistant',
        content: () =>
          msg(
            'Klarvos categorizes your transactions, detects business expenses, and applies the latest IRS rules for Schedule C deductions. It auto-tracks mileage, home office, and recurring subscriptions. You can review flags and override any category before export.'
          ),
      },
      {
        role: 'user',
        content: () =>
          msg(
            'Can you give me an example with $85k revenue and $22k expenses?'
          ),
      },
      {
        role: 'assistant',
        content: () =>
          msg(
            "- Revenue: $85,000\n- Deductible expenses detected: $22,000 (subscriptions, equipment, mileage)\n- Net profit: $63,000\n- Estimated SE tax: ~$9,600\n- Quarterly estimates: ~$2,400 each\n\nYou'll get a detailed breakdown plus a ready-to-file Schedule C export."
          ),
      },
    ],
  },
  {
    id: 'import-cc',
    title: () => msg('Import Credit Card Statements'),
    messages: [
      {
        role: 'user',
        content: () => msg('Import these AMEX statements for U5 GmbH'),
        attachments: [{ name: 'credit-card-statement-december.pdf' }],
      },
      {
        role: 'assistant',
        content: () =>
          msg(
            "Sure, I've created a batch with 19 transactions for December. Want to me to show the results in Rechnungswesen?"
          ),
      },
    ],
  },
  {
    id: 'import-dms',
    title: () => msg('Import files to DMS'),
    messages: [
      {
        role: 'user',
        content: () => msg('Import these files into the DMS'),
        attachmentsCount: 321,
      },
      {
        role: 'assistant',
        content: () =>
          msg(
            'To what client should I import them, looks like all belong to U5 GmbH, is that correct?'
          ),
      },
      {
        role: 'user',
        content: () =>
          msg('Yes, U5. Thanks a lot, you saved me 5 days of work!'),
      },
    ],
  },
  {
    id: 'create-client',
    title: () => msg('How to create clients'),
    messages: [
      {
        role: 'user',
        content: () => msg('How can I create a new client in DATEV?'),
      },
      {
        role: 'assistant',
        content: () =>
          msg(
            'To create a new client, open the “Mandantenübersicht” worksheet and click “Mandant anlegen” at the top or select it via right-click in the menu.\nThe “Stammdaten-Mandant” window will open.'
          ),
        imageUrl: '/mock/create-client.png',
      },
    ],
  },
  {
    id: 'welcome',
    title: () => msg('Welcome'),
    messages: [
      {
        role: 'assistant',
        content: () => msg('Hi! Ask me anything about your finances.'),
      },
    ],
  },
];

const STATIC_SEED_FOR_ENV = isProduction() ? [] : STATIC_CONVERSATION_SEED;

function buildStaticConversation(
  seed: (typeof STATIC_CONVERSATION_SEED)[number],
  appends: StaticConversationMessage[] = []
): Conversation {
  return {
    id: seed.id,
    title: resolveStaticConversationText(seed.title),
    source: 'static',
    toolSelection: { ...EMPTY_CHAT_TOOL_SELECTION, mcpServerIds: [] },
    messages: [...seed.messages, ...appends].map((message) => ({
      ...message,
      content: resolveStaticConversationText(message.content),
    })),
  };
}

function resolveStaticConversationText(value: StaticConversationText): string {
  return typeof value === 'function' ? value() : value;
}

@customElement('kv-module-chat')
@localized()
export class KvModuleChat extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      display: flex;
      height: 100%;
      min-height: 0;
      --mobile-input-reserve: 0px;
      --chat-surface: var(--kv-surface);
      --chat-surface-muted: var(--kv-surface-muted);
      --chat-surface-hover: var(--kv-surface-hover);
      --chat-accent: var(--kv-accent);
      --chat-accent-bg: var(--kv-accent-bg);
      --chat-border: var(--kv-border);
      --chat-shadow: var(--kv-shadow);
      --chat-assistant-text: #2f3440;
      --chat-assistant-strong: #202633;
      --chat-inline-code-bg: var(--kv-accent-bg);
      --chat-inline-code-border: var(--kv-control-border);
      --chat-inline-code-text: #273b8f;
      --chat-user-bubble-bg: var(--kv-accent-bg);
      --chat-user-bubble-border: var(--kv-accent-border-subtle);
      --chat-user-bubble-text: #263247;
      --chat-user-bubble-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
    }
    :host-context(sp-theme[color='dark']) {
      --chat-assistant-text: var(--color-chat-text);
      --chat-assistant-strong: var(--color-chat-text);
      --chat-inline-code-bg: rgba(138, 164, 255, 0.16);
      --chat-inline-code-border: rgba(138, 164, 255, 0.24);
      --chat-inline-code-text: #dbe5ff;
      --chat-user-bubble-bg: var(--color-chat-user-bg);
      --chat-user-bubble-border: rgba(255, 255, 255, 0.16);
      --chat-user-bubble-text: var(--color-chat-text);
      --chat-user-bubble-shadow: none;
    }
    sp-split-view.chat-split {
      flex: 1;
      min-height: 0;
      height: 100vh;
      background: var(--color-chat-chat-area-bg);
      --mod-splitview-handle-background-color: var(--color-app-border);
      --mod-splitview-handle-background-color-hover: var(--spectrum-gray-500);
    }
    .conversations {
      min-height: 0;
      min-width: 0;
      box-sizing: border-box;
      background:
        linear-gradient(
          180deg,
          var(--chat-surface) 0%,
          var(--chat-surface-muted) 100%
        );
      overflow-x: hidden;
      overflow-y: auto;
    }
    .conversations .new-chat {
      position: sticky;
      top: 0;
      z-index: 1;
      padding: 12px 12px 8px;
      background: inherit;
    }
    .new-chat-button {
      width: 100%;
      min-height: 34px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      box-sizing: border-box;
      border: 1px solid var(--kv-control-border);
      border-radius: 8px;
      border-color: var(--kv-control-border);
      background: var(--chat-accent-bg);
      color: var(--chat-accent);
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      font-weight: 700;
      transition:
        background 0.14s ease,
        border-color 0.14s ease,
        color 0.14s ease;
    }
    .new-chat-button:hover {
      border-color: var(--kv-accent-border);
      background: var(--chat-surface-hover);
    }
    .new-chat-button:focus-visible {
      outline: 2px solid var(--chat-accent);
      outline-offset: 2px;
    }
    .new-chat-button:disabled {
      cursor: not-allowed;
      opacity: 0.56;
    }
    .new-chat-button sp-icon-add {
      width: 14px;
      height: 14px;
    }
    .conversations .conversation-list {
      list-style: none;
      margin: 0;
      padding: 6px 10px 14px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
    .conversation-row {
      position: relative;
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      width: 100%;
    }
    .conversation-item {
      display: flex;
      align-items: center;
      width: 100%;
      min-width: 0;
      min-height: 46px;
      box-sizing: border-box;
      padding: 8px 38px 8px 10px;
      border: 1px solid transparent;
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: start;
      border-radius: 8px;
      cursor: pointer;
      overflow: hidden;
      transition:
        background 0.14s ease,
        border-color 0.14s ease,
        color 0.14s ease;
    }
    .conversation-item:hover {
      background: var(--chat-surface-hover);
      border-color: var(--kv-accent-border-subtle);
    }
    .conversation-item.is-selected {
      background: var(--chat-accent-bg);
      border-color: var(--kv-accent-border);
      color: var(--chat-accent);
    }
    .conversation-item:focus-visible {
      outline: 2px solid var(--chat-accent);
      outline-offset: 2px;
    }
    .conversation-content {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 3px;
      min-width: 0;
    }
    .conversation-item-title {
      color: var(--color-app-text);
      font-size: 13px;
      font-weight: 600;
      line-height: 1.25;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .conversation-item.is-selected .conversation-item-title {
      color: var(--chat-accent);
    }
    .conversation-item-meta {
      color: var(--color-app-text-secondary);
      font-size: 11px;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .conversation-delete {
      display: none;
      flex-shrink: 0;
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      --spectrum-actionbutton-min-width: 28px;
      --spectrum-actionbutton-height: 28px;
    }
    .conversation-row:hover .conversation-delete,
    .conversation-row:focus-within .conversation-delete {
      display: inline-flex;
    }
    @media (hover: none), (pointer: coarse) {
      .conversation-delete {
        display: inline-flex;
      }
    }
    .conversation-delete[disabled] {
      display: inline-flex;
      opacity: 0.55;
    }
    .chat-area {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      background:
        linear-gradient(
          180deg,
          var(--color-chat-chat-area-bg) 0%,
          var(--chat-surface-muted) 100%
        );
      color: var(--color-chat-text);
      position: relative;
      *, *::after, *::before {
        user-select: text;
        -webkit-user-select: text;
        cursor: auto;
      }
    }
    .chat-area.drag-active::before {
      content: 'Drop files to attach';
      position: absolute;
      inset: 18px;
      border: 2px dashed var(--chat-accent);
      border-radius: 12px;
      background: color-mix(in srgb, var(--kv-accent-bg) 72%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--chat-accent);
      font-size: 16px;
      font-weight: 600;
      pointer-events: none;
      z-index: 1;
    }
    .chat-area.models-open {
      background:
        linear-gradient(
          180deg,
          var(--color-chat-chat-area-bg) 0%,
          var(--chat-surface-muted) 100%
        );
    }
    .models-workspace {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .models-workspace-body {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
    .models-workspace-body kv-module-custom-models {
      height: 100%;
    }
    .messages {
      flex: 1;
      min-height: 0;
      padding: 24px 28px 14px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
    }
    kv-chat-user-message,
    kv-chat-assistant-message {
      display: contents;
    }
    .messages.empty {
      align-items: center;
      justify-content: center;
      color: var(--color-chat-placeholder-text);
      font-size: 14px;
    }
    .empty-state {
      width: min(420px, 100%);
      padding: 22px;
      border: 1px dashed var(--chat-border);
      border-radius: 10px;
      background: var(--chat-surface);
      box-shadow: var(--chat-shadow);
      box-sizing: border-box;
      text-align: center;
    }
    .empty-state-title {
      margin: 0 0 6px;
      color: var(--color-app-text);
      font-size: 16px;
      font-weight: 700;
      line-height: 1.25;
    }
    .empty-state-text {
      margin: 0;
      color: var(--color-app-text-secondary);
      font-size: 13px;
      line-height: 1.45;
    }
    .conversation-title {
      box-sizing: border-box;
      width: min(960px, 100%);
      margin: 0 auto 22px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--chat-border);
      color: var(--color-app-text);
      font-size: 18px;
      font-weight: 750;
      line-height: 1.25;
    }
    .message {
      width: min(900px, 100%);
      margin: 0 auto 18px;
      padding: 0;
      box-sizing: border-box;
      line-height: 1.5;
      display: inline-block;
      align-self: flex-start;
      overflow-wrap: break-word;
      word-break: break-word;
    }
    .message .markdown {
      display: block;
      white-space: normal;
      line-height: 1.6;
      overflow-wrap: break-word;
      word-break: break-word;
    }
    .message .markdown p {
      margin: 0 0 var(--gap-1);
    }
    .message .markdown p:last-child {
      margin-bottom: 0;
    }
    .message .markdown ul,
    .message .markdown ol {
      margin: 0 0 var(--gap-1) 1.25rem;
      padding: 0;
    }
    .message .markdown li + li {
      margin-top: 4px;
    }
    .message .markdown pre {
      margin: 0 0 var(--gap-1);
      padding: 12px;
      border: 1px solid var(--chat-border);
      border-radius: 8px;
      background: var(--color-chat-code-bg, rgba(0, 0, 0, 0.35));
      overflow-x: auto;
      font-size: 13px;
    }
    .message .markdown code {
      font-family: var(--font-monospace, 'Source Code Pro', monospace);
      font-size: 13px;
      background: var(--color-chat-code-inline-bg, rgba(0, 0, 0, 0.2));
      padding: 2px 4px;
      border-radius: 6px;
    }
    .message .image-ph {
      margin-top: var(--gap-2);
      width: 100%;
      height: 160px;
      border: 2px dashed var(--color-chat-placeholder-border);
      border-radius: 12px;
      background: var(--color-chat-placeholder-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-chat-placeholder-text);
      font-size: 14px;
    }
    .message .attachments {
      display: inline-flex;
      align-items: flex-start;
      gap: 8px;
      margin-top: var(--gap-2);
      padding: 8px 12px;
      border-radius: 999px;
      border: var(--border);
      background: var(--color-chat-attachments-bg);
      font-size: 12px;
      color: var(--color-chat-attachments-text);
    }
    .message .attachments sp-icon-attach {
      width: 16px;
      height: 16px;
      color: var(--color-chat-attachments-icon);
      flex-shrink: 0;
    }
    .message .attachments .meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .message .attachments .status {
      font-size: 11px;
      color: var(--spectrum-gray-700, #4e4a57);
    }
    .message .attachments .status.saved {
      color: var(--spectrum-green-700, #268e6c);
    }
    .message .attachments .status.failed {
      color: var(--spectrum-red-600, #c9252d);
    }
    .message .attachments .status.ignored {
      color: var(--spectrum-gray-600, #605766);
    }
    .message .attachments .status.pending {
      color: var(--spectrum-gray-700, #4e4a57);
    }
    .message img.image-attachment {
      display: block;
      margin-top: var(--gap-2);
      max-width: 400px;
      width: 100%;
      height: auto;
      border-radius: 12px;
      border: var(--border);
      background: var(--color-chat-image-bg);
    }
    ${assistantMessageStyles}
    .assistant {
      background: var(--color-chat-assistant-bg);
      color: var(--chat-assistant-text);
    }
    .assistant .markdown {
      color: var(--chat-assistant-text);
    }
    .assistant .markdown strong {
      color: var(--chat-assistant-strong);
      font-weight: 750;
    }
    .assistant .markdown code {
      border: 1px solid var(--chat-inline-code-border);
      background: var(--chat-inline-code-bg);
      color: var(--chat-inline-code-text);
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }
    .user {
      background: var(--chat-user-bubble-bg);
      border: 1px solid var(--chat-user-bubble-border);
      border-radius: 16px 16px 4px 16px;
      box-shadow: var(--chat-user-bubble-shadow);
      color: var(--chat-user-bubble-text);
      max-width: min(560px, 72%);
      text-align: right;
      align-self: flex-end;
      margin-right: max(0px, calc((100% - 960px) / 2));
      padding: 10px 14px;
      width: fit-content;
    }
    .status {
      flex-shrink: 0;
      margin: 10px 18px 0;
      padding: 10px 12px;
      border: 1px solid rgba(215, 55, 63, 0.2);
      border-radius: 8px;
      background: var(--color-error-bg);
      font-size: 13px;
      color: var(--color-error-text);
    }
    .input {
      flex-shrink: 0;
      display: block;
      width: 100%;
      padding: 10px 18px 14px;
      border-top: 1px solid var(--chat-border);
      background:
        linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          var(--chat-surface-muted) 36%
        );
      box-sizing: border-box;
    }
    .composer {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
      position: relative;
      box-sizing: border-box;
      padding: 8px;
      border: 1px solid var(--chat-border);
      border-radius: 10px;
      background: var(--chat-surface);
      box-shadow: var(--chat-shadow);
    }
    .composer textarea {
      box-sizing: border-box;
      width: 100%;
      resize: none;
      min-height: 48px;
      max-height: 150px;
      padding: 6px 8px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: inherit;
      font: inherit;
      outline: none;
    }
    .composer textarea::placeholder {
      color: var(--color-app-text-secondary);
    }
    .composer textarea:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .composer.dragging textarea {
      background: color-mix(in srgb, var(--kv-accent-bg) 72%, transparent);
    }
    .composer-toolbar {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: space-between;
      min-width: 0;
    }
    .composer-context,
    .composer-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .composer-context {
      flex: 1 1 auto;
      overflow: hidden;
    }
    .composer-actions {
      flex: 0 0 auto;
    }
    .composer-actions sp-action-button {
      --spectrum-actionbutton-min-width: 32px;
      --spectrum-actionbutton-height: 32px;
    }
    .composer-actions .send-button:not([disabled]) {
      color: var(--chat-accent);
    }
    .composer-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      max-width: 220px;
      height: 30px;
      box-sizing: border-box;
      padding: 0 9px;
      border: 1px solid var(--kv-accent-border-subtle);
      border-radius: 999px;
      background: var(--chat-accent-bg);
      color: var(--chat-accent);
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
    }
    .composer-chip,
    .composer-chip * {
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
    }
    .composer-chip:hover {
      border-color: var(--kv-accent-border);
      background: var(--chat-surface-hover);
    }
    .composer-chip:focus-visible {
      outline: 2px solid var(--chat-accent);
      outline-offset: 2px;
    }
    .composer-chip[disabled] {
      cursor: not-allowed;
      opacity: 0.56;
    }
    .composer-chip[disabled] * {
      cursor: not-allowed;
    }
    .composer-chip-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .composer-chip sp-icon-chevron-down {
      flex: 0 0 auto;
      width: 12px;
      height: 12px;
    }
    .composer-menu sp-menu {
      min-width: 260px;
    }
    .composer-menu sp-menu-item,
    .composer-menu sp-menu-item * {
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
    }
    .composer-menu sp-menu-item[disabled],
    .composer-menu sp-menu-item[disabled] * {
      cursor: default;
    }
    .manage-models-menu-item {
      border-top: 1px solid var(--chat-border);
      margin-top: 4px;
      color: var(--chat-accent);
      font-weight: 700;
    }
    .menu-section-label {
      color: var(--color-app-text-secondary);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0;
    }
    .attachment-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .attachment-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 999px;
      border: var(--border);
      background: var(--color-chat-attachments-bg);
      color: var(--color-chat-attachments-text);
      font-size: 12px;
    }
    .attachment-chip sp-icon-attach {
      width: 16px;
      height: 16px;
      color: var(--color-chat-attachments-icon);
    }
    .attachment-chip .remove {
      border: none;
      background: none;
      color: var(--color-chat-attachments-icon);
      cursor: pointer;
      padding: 0;
      line-height: 1;
      font-size: 16px;
    }
    .attachment-chip .remove:hover {
      color: var(--color-chat-text);
    }
    .audio-status {
      font-size: 12px;
      color: var(--color-chat-placeholder-text, #6b7280);
      padding: 0 var(--gap-2);
    }
    .message .assistant-audio {
      display: block;
      margin-top: var(--gap-2);
      max-width: 100%;
    }

    /* Mobile-only UI elements (default hidden; enabled in media query) */
    .mobile-topbar {
      display: none;
    }

    .backdrop {
      display: none;
    }
    /* Only desktop */
    @media (min-width: 769px) {
      .conversations {
        z-index: 1;
      }
    }
    /* Minimal mobile support: keep the chat usable on small screens (KLA-376) */
    @media (max-width: 768px) {
      :host {
        position: relative;
        height: 100%;
        min-height: 0;
      }

      .conversation-title {
        width: 100%;
        margin-bottom: 16px;
        padding-left: var(--kv-mobile-title-inline-start);
      }

      /* Hide sidebar by default; show as a drawer when toggled */
      .conversations {
        display: none;
      }

      :host(.mobile-sidebar-open) .conversations {
        display: block;
        position: fixed;
        top: 0;
        left: var(--kv-module-switcher-width);
        height: 100dvh;
        width: min(calc(92vw - var(--kv-module-switcher-width)), 360px);
        max-width: calc(100vw - var(--kv-module-switcher-width));
        z-index: 50;
        border-right: 1px solid var(--color-chat-input-border);
      }

      :host(.mobile-sidebar-open) .backdrop {
        display: block;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 40;
      }

      .messages {
        padding: var(--gap-2);
        padding-bottom: calc(var(--gap-2) + var(--mobile-input-reserve));
      }

      .message {
        width: 100%;
      }

      .user {
        max-width: 88%;
        margin-right: 0;
      }

      .composer textarea {
        /* Prevent iOS Safari from zooming into inputs */
        font-size: 16px;
        min-height: 72px;
      }

      .composer-toolbar {
        align-items: flex-start;
        gap: 8px;
      }

      .composer-context {
        flex-wrap: wrap;
      }

      .composer-chip {
        max-width: min(180px, 42vw);
      }

      /* Reserve space above iOS browser toolbar; safe-area-inset-bottom alone often
         only covers the home indicator, not the Safari nav bar */
      .input {
        padding-left: calc(var(--gap-2) + env(safe-area-inset-left, 0px));
        padding-right: calc(var(--gap-2) + env(safe-area-inset-right, 0px));
        padding-bottom: calc(var(--gap-2) + env(safe-area-inset-bottom));
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        align-items: flex-end;
      }

      kv-confirmation-dialog {
        padding: var(--gap-2);
      }
    }
    `,
  ];

  private get staticConversations(): Conversation[] {
    return STATIC_SEED_FOR_ENV.map((seed) =>
      buildStaticConversation(seed, this.staticConversationAppends[seed.id])
    );
  }

  @state()
  accessor staticConversationAppends: Record<
    string,
    StaticConversationMessage[]
  > = {};

  @state()
  accessor dbConversations: Conversation[] = [];

  @state()
  accessor selectedId: string | null = STATIC_SEED_FOR_ENV[0]?.id ?? null;

  @state()
  accessor inputText = '';

  @state()
  accessor isSending = false;

  @state()
  accessor isCreating = false;

  @state()
  accessor errorMessage: string | null = null;

  @state()
  accessor pendingAttachments: PendingAttachment[] = [];

  @state()
  accessor isDragActive = false;

  @property({ type: Boolean })
  accessor mobileSidebarOpen = false;

  @state()
  accessor audioStatus: 'idle' | 'recording' | 'sending' | 'speaking' = 'idle';

  @state()
  accessor pendingAudioMessageId: string | null = null;

  @state()
  accessor pendingAssistantConversationId: string | null = null;

  @state()
  accessor chatModelsConfig: ChatModelsConfig | null = null;

  @state()
  accessor activeModel: string = FALLBACK_DEFAULT_MODEL;

  @state() accessor activeCustomModelId: string | null = null;

  @state()
  accessor activeView: ChatView = 'conversation';

  @state()
  accessor confirmDeleteConversationId: string | null = null;

  @state()
  accessor isDeletingConversation = false;

  @state()
  accessor mcpServers: McpServerConfig[] = [];

  @state()
  accessor isUpdatingToolSelection = false;

  private dragDepth = 0;
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null;
  private recordingStartTime: number | null = null;
  private readonly onWindowResize = () => this.updateInputReserve();
  private readonly onPopState = () => this.syncViewFromUrl();

  connectedCallback(): void {
    super.connectedCallback();
    this.syncViewFromUrl();
    void this.loadChatModels();
    void this.loadConversations();
    void this.loadMcpServers();
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback(): void {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('popstate', this.onPopState);
    super.disconnectedCallback();
  }

  private get defaultModel(): string {
    return this.chatModelsConfig?.defaultModel ?? FALLBACK_DEFAULT_MODEL;
  }

  private async loadChatModels(): Promise<void> {
    try {
      const res = await fetch('/api/v1/chat/models', {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as ChatModelsConfig;
      if (data?.defaultModel && Array.isArray(data?.models)) {
        this.chatModelsConfig = {
          ...data,
          customModels: Array.isArray(data.customModels)
            ? data.customModels
            : [],
        };
        if (this.activeCustomModelId) {
          const stillExists = this.chatModelsConfig.customModels?.some(
            (c) => c.id === this.activeCustomModelId
          );
          if (!stillExists) this.activeCustomModelId = null;
        }
        if (
          !this.activeCustomModelId &&
          !this.chatModelsConfig.models.some((m) => m.id === this.activeModel)
        ) {
          this.activeModel = this.chatModelsConfig.defaultModel;
        }
        this.syncModelPickerFromSelectedConversation();
      }
    } catch {
      // keep fallback default and empty config
    }
  }

  private async loadMcpServers(): Promise<void> {
    try {
      const servers = await api.mcpServers.listServers();
      this.mcpServers = Array.isArray(servers)
        ? getAvailableMcpServers(servers)
        : [];
    } catch {
      this.mcpServers = [];
    }
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('mobileSidebarOpen')) {
      this.classList.toggle('mobile-sidebar-open', this.mobileSidebarOpen);
    }
    if (changed.has('activeView')) {
      this.updateChatViewUrl();
    }
    this.updateInputReserve();
  }

  private syncViewFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    this.activeView =
      params.get('chatView') === 'models' ? 'models' : 'conversation';
  }

  private updateChatViewUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.set('module', 'chat');
    if (this.activeView === 'models') {
      url.searchParams.set('chatView', 'models');
    } else {
      url.searchParams.delete('chatView');
    }
    window.history.replaceState({}, '', url);
  }

  private get allConversations(): Conversation[] {
    return [...this.dbConversations, ...this.staticConversations];
  }

  private get selectedConversation(): Conversation | undefined {
    if (!this.selectedId) return undefined;
    return this.allConversations.find((conv) => conv.id === this.selectedId);
  }

  private get activeModelLabel(): string {
    if (this.activeCustomModelId) {
      const custom = this.chatModelsConfig?.customModels?.find(
        (model) => model.id === this.activeCustomModelId
      );
      return custom
        ? this.getCustomModelOptionLabel(custom)
        : this.activeCustomModelId;
    }
    return this.localizeModelLabel(
      this.chatModelsConfig?.models.find(
        (model) => model.id === this.activeModel
      )?.label ?? this.activeModel
    );
  }

  private localizeModelLabel(label: string): string {
    const defaultMatch = /^Default \((.+)\)$/.exec(label);
    if (!defaultMatch) {
      return label;
    }
    return msg(str`Default (${defaultMatch[1]})`);
  }

  private getCustomModelOptionLabel(option: CustomModelOption): string {
    if (option.baseModelLabel && option.versionLabel) {
      return `${option.label} · ${this.localizeModelLabel(
        option.baseModelLabel
      )} · ${option.versionLabel}`;
    }
    return option.menuLabel ?? option.label;
  }

  private isModelOptionSelected(option: ChatModelOption): boolean {
    return !this.activeCustomModelId && this.activeModel === option.id;
  }

  private get activeToolSelection(): ChatToolSelection {
    return (
      this.selectedConversation?.toolSelection ?? EMPTY_CHAT_TOOL_SELECTION
    );
  }

  private get selectedToolSourceCount(): number {
    return getSelectedToolSourceCount(
      this.activeToolSelection,
      this.mcpServers
    );
  }

  private get toolsLabel(): string {
    const count = this.selectedToolSourceCount;
    return count === 0 ? msg('Tools') : msg(str`Tools · ${count}`);
  }

  private getConversationMeta(conversation: Conversation): string {
    const messageCount = conversation.messages.filter(
      (message) => message.role !== 'system'
    ).length;
    const countLabel =
      messageCount === 1
        ? msg('1 message')
        : msg(str`${messageCount} messages`);
    return conversation.source === 'static'
      ? `${countLabel} · ${msg('Example')}`
      : countLabel;
  }

  private deriveModelFromMessages(conversation: Conversation): string {
    const lastAssistantModel = [...conversation.messages]
      .reverse()
      .find(
        (message) => message.role === 'assistant' && !!message.model
      )?.model;
    if (
      typeof lastAssistantModel === 'string' &&
      this.chatModelsConfig?.models.some((m) => m.id === lastAssistantModel)
    ) {
      return lastAssistantModel;
    }
    return this.defaultModel;
  }

  private syncModelPickerFromSelectedConversation(): void {
    const conversation = this.selectedConversation;
    if (!conversation || conversation.source !== 'database') {
      return;
    }
    const cid = conversation.customModelId;
    const customs = this.chatModelsConfig?.customModels;

    if (!cid) {
      this.activeCustomModelId = null;
      this.activeModel = this.deriveModelFromMessages(conversation);
      return;
    }

    if (customs?.length) {
      const custom = customs.find((c) => c.id === cid);
      if (custom) {
        this.activeCustomModelId = cid;
        this.activeModel = custom.baseModel;
      } else {
        this.activeCustomModelId = null;
        this.activeModel = this.deriveModelFromMessages(conversation);
      }
      return;
    }

    this.activeCustomModelId = cid;
  }

  private async loadConversations(): Promise<void> {
    try {
      const res = await fetch('/api/v1/chat/conversations', {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }
      const payload = (await res.json()) as ApiConversation[];
      const mapped = payload.map((conversation) =>
        this.mapApiConversation(conversation)
      );
      this.dbConversations = mapped;
      if (!this.selectedId || !this.selectedConversation) {
        this.selectedId = mapped[0]?.id ?? this.selectedId;
      }
      const selected = this.selectedConversation;
      if (selected?.source === 'database') {
        this.syncModelPickerFromSelectedConversation();
      } else {
        this.activeCustomModelId = null;
        this.activeModel = selected
          ? this.deriveModelFromMessages(selected)
          : this.defaultModel;
      }
      this.scrollMessagesToBottom();
    } catch (error) {
      console.error('Failed to load conversations', error);
      this.errorMessage = msg(
        'Unable to load your recent chats. Start a new one to begin.'
      );
    }
  }

  private mapApiConversation(data: ApiConversation): Conversation {
    return {
      id: data.id,
      title: data.title,
      source: 'database',
      customModelId: data.customModelId ?? null,
      toolSelection: normalizeChatToolSelection(data.toolSelection),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      messages: data.messages.map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        model: message.model,
        createdAt: message.createdAt,
        attachments: message.attachments?.map((attachment) => ({
          name: attachment.name,
          url: attachment.url,
          status: attachment.status,
          message: attachment.message,
          mimeType: attachment.mimeType,
        })),
        toolCalls: message.toolCalls,
      })),
    };
  }

  private addFiles(files: File[]): void {
    const valid = files.filter((file) => file?.name);
    if (!valid.length) return;
    const hasRandomUuid = typeof globalThis.crypto?.randomUUID === 'function';
    const mapped = valid.map((file) => ({
      id: hasRandomUuid
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
    }));
    this.pendingAttachments = [...this.pendingAttachments, ...mapped];
    this.errorMessage = null;
    this.scheduleComposerAdjust();
    this.focusComposer();
  }

  private removeAttachment(id: string): void {
    this.pendingAttachments = this.pendingAttachments.filter(
      (attachment) => attachment.id !== id
    );
    this.scheduleComposerAdjust();
  }

  private onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length) {
      this.addFiles(files);
    }
    input.value = '';
  }

  private openFilePicker(): void {
    const input = this.renderRoot?.querySelector(
      '#chat-attachment-input'
    ) as HTMLInputElement | null;
    input?.click();
  }

  private async startRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaStream = stream;
      this.audioChunks = [];
      this.recordingStartTime = Date.now();
      const recorder = new MediaRecorder(stream);
      this.mediaRecorder = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };
      recorder.start(200);
      this.audioStatus = 'recording';
      this.maxDurationTimer = setTimeout(() => {
        this.stopRecording();
      }, 60_000);
    } catch (err) {
      console.error('Failed to start recording', err);
      this.errorMessage = 'Microphone access denied or unavailable.';
    }
  }

  private stopRecording(): void {
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = null;
    }
    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    const startTime = this.recordingStartTime;
    this.recordingStartTime = null;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.onstop = () => {
      this.mediaStream?.getTracks().forEach((t) => {
        t.stop();
      });
      this.mediaStream = null;
      const blob =
        this.audioChunks.length > 0
          ? new Blob(this.audioChunks, { type: recorder.mimeType })
          : null;
      if (!blob) {
        this.audioStatus = 'idle';
        return;
      }
      const durationSeconds =
        startTime != null ? (Date.now() - startTime) / 1000 : 0;
      if (durationSeconds < MIN_AUDIO_DURATION_SECONDS) {
        this.errorMessage = 'Recording too short.';
        this.audioStatus = 'idle';
        return;
      }
      void this.sendAudioBlob(blob);
    };
    recorder.stop();
  }

  private async sendAudioBlob(blob: Blob): Promise<void> {
    const conversation = this.selectedConversation;
    if (!conversation || conversation.source !== 'database') {
      this.audioStatus = 'idle';
      return;
    }
    this.audioStatus = 'sending';
    this.errorMessage = null;
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice.webm');
      formData.append('conversationId', conversation.id);
      const res = await fetch('/api/v1/chat/audio', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = (await res.json()) as {
        transcript?: string;
        error?: string;
      };
      if (!res.ok) {
        this.errorMessage =
          data.error ?? 'Voice message failed. Please try again.';
        this.audioStatus = 'idle';
        return;
      }
      const transcript = data.transcript ?? '';
      this.audioStatus = 'idle';
      if (!transcript) return;
      const conv = this.ensureConversation(conversation.id);
      await this.sendLiveMessage(conv.id, transcript);
    } catch (err) {
      console.error('Failed to send voice message', err);
      this.errorMessage = 'Voice message failed. Please try again.';
      this.audioStatus = 'idle';
    }
  }

  private onMicClick(): void {
    if (this.audioStatus === 'recording') {
      this.stopRecording();
      return;
    }
    if (this.audioStatus === 'idle') {
      void this.startRecording();
    }
  }

  private closeOverlayFromEvent(event: Event): void {
    const target = event.currentTarget as HTMLElement | null;
    target?.dispatchEvent(
      new Event('close', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private openCustomModelsView(event?: Event): void {
    if (event) this.closeOverlayFromEvent(event);
    this.activeView = 'models';
    this.requestCloseMobileNavigation();
  }

  private closeCustomModelsView(focusComposer = true): void {
    this.activeView = 'conversation';
    if (focusComposer) this.scheduleComposerAdjust(true);
  }

  private onCustomModelsChanged = (): void => {
    void this.loadChatModels();
  };

  private hasFileDrag(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private onDragEnter(event: DragEvent): void {
    if (this.activeView === 'models') return;
    if (!this.hasFileDrag(event)) return;
    event.preventDefault();
    this.dragDepth += 1;
    this.isDragActive = true;
  }

  private onDragOver(event: DragEvent): void {
    if (this.activeView === 'models') return;
    if (!this.hasFileDrag(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  private onDragLeave(event: DragEvent): void {
    if (this.activeView === 'models') return;
    if (!this.hasFileDrag(event)) return;
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.isDragActive = false;
    }
  }

  private onDrop(event: DragEvent): void {
    if (this.activeView === 'models') return;
    if (!this.hasFileDrag(event)) return;
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.dragDepth = 0;
    this.isDragActive = false;
    if (files.length) {
      this.addFiles(files);
    }
  }

  private updateDbConversation(conversation: Conversation): void {
    this.dbConversations = [
      conversation,
      ...this.dbConversations.filter((c) => c.id !== conversation.id),
    ];
    this.selectedId = conversation.id;
  }

  private async saveToolSelection(selection: ChatToolSelection): Promise<void> {
    const conversation = this.selectedConversation;
    if (conversation?.source !== 'database' || this.isUpdatingToolSelection) {
      return;
    }

    this.isUpdatingToolSelection = true;
    this.errorMessage = null;
    try {
      const persisted = await persistChatToolSelection(
        conversation.id,
        selection
      );
      this.dbConversations = this.dbConversations.map((candidate) =>
        candidate.id === conversation.id
          ? { ...candidate, toolSelection: persisted }
          : candidate
      );
    } catch {
      this.errorMessage = msg('Failed to update tools. Please try again.');
    } finally {
      this.isUpdatingToolSelection = false;
      this.scheduleComposerAdjust();
    }
  }

  private toggleKnowledgeTool(
    tool: 'taxKnowledgeEnabled' | 'datevKnowledgeEnabled'
  ): void {
    const selection = this.activeToolSelection;
    void this.saveToolSelection({
      ...selection,
      mcpServerIds: [...selection.mcpServerIds],
      [tool]: !selection[tool],
    });
  }

  private toggleMcpServer(serverId: number): void {
    const selection = this.activeToolSelection;
    const selected = selection.mcpServerIds.includes(serverId);
    void this.saveToolSelection({
      ...selection,
      mcpServerIds: selected
        ? selection.mcpServerIds.filter((id) => id !== serverId)
        : [...selection.mcpServerIds, serverId],
    });
  }

  private requestCloseMobileNavigation(): void {
    this.dispatchEvent(
      new CustomEvent('close-mobile-module-switcher', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private selectConversation(id: string): void {
    this.selectedId = id;
    if (this.activeView === 'models') {
      this.closeCustomModelsView(false);
    }
    const conversation = this.allConversations.find((conv) => conv.id === id);
    if (conversation?.source === 'database') {
      this.syncModelPickerFromSelectedConversation();
    } else {
      this.activeCustomModelId = null;
      this.activeModel = conversation
        ? this.deriveModelFromMessages(conversation)
        : this.defaultModel;
    }
    this.inputText = '';
    this.errorMessage = null;
    this.pendingAttachments = [];
    this.dragDepth = 0;
    this.isDragActive = false;
    // On mobile, close the history drawer after selecting a conversation.
    this.requestCloseMobileNavigation();
    this.scheduleComposerAdjust(true);
    this.scrollMessagesToBottom();
  }

  private async startNewConversation(): Promise<void> {
    this.errorMessage = null;
    if (this.activeView === 'models') {
      this.closeCustomModelsView(false);
    }
    this.isCreating = true;
    try {
      const res = await fetch('/api/v1/chat/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }
      const created = (await res.json()) as ApiConversation;
      const conversation = this.mapApiConversation(created);
      this.updateDbConversation(conversation);
      this.activeCustomModelId = null;
      this.activeModel = this.defaultModel;
      this.clearInput();
      this.scrollMessagesToBottom();
    } catch (error) {
      console.error('Failed to create chat conversation', error);
      this.errorMessage =
        error instanceof Error
          ? error.message || 'Failed to start a new chat.'
          : 'Failed to start a new chat.';
    } finally {
      this.isCreating = false;
      this.scheduleComposerAdjust(true);
    }
  }

  private promptDeleteConversation(conversationId: string, event: Event): void {
    event.stopPropagation();
    this.confirmDeleteConversationId = conversationId;
    this.errorMessage = null;
  }

  private cancelDeleteConversation(): void {
    if (this.isDeletingConversation) return;
    this.confirmDeleteConversationId = null;
  }

  private async confirmDeleteConversation(): Promise<void> {
    const conversationId = this.confirmDeleteConversationId;
    if (!conversationId) return;

    this.isDeletingConversation = true;
    this.errorMessage = null;
    try {
      const res = await fetch(`/api/v1/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error((await res.text()) || 'Failed to delete chat.');
      }

      const wasSelected = this.selectedId === conversationId;
      this.dbConversations = this.dbConversations.filter(
        (conversation) => conversation.id !== conversationId
      );

      if (wasSelected) {
        const nextSelectedId =
          this.dbConversations[0]?.id ??
          this.staticConversations[0]?.id ??
          null;
        this.selectedId = nextSelectedId;
        const nextConversation = this.selectedConversation;
        if (nextConversation?.source === 'database') {
          this.syncModelPickerFromSelectedConversation();
        } else {
          this.activeCustomModelId = null;
          this.activeModel = nextConversation
            ? this.deriveModelFromMessages(nextConversation)
            : this.defaultModel;
        }
        this.clearInput();
      }
      this.confirmDeleteConversationId = null;
    } catch (error) {
      console.error('Failed to delete chat conversation', error);
      this.errorMessage =
        error instanceof Error
          ? error.message || 'Failed to delete chat conversation.'
          : 'Failed to delete chat conversation.';
    } finally {
      this.isDeletingConversation = false;
    }
  }

  private async onSend(): Promise<void> {
    const text = this.inputText.trim();
    if (!text || this.isUpdatingToolSelection) return;
    const conversation = this.selectedConversation;
    if (!conversation) return;

    if (conversation.source === 'static') {
      this.appendStaticMessage(conversation.id, text);
      return;
    }

    await this.sendLiveMessage(conversation.id, text);
  }

  private appendStaticMessage(conversationId: string, text: string): void {
    const currentMessages =
      this.staticConversationAppends[conversationId] ?? [];
    this.staticConversationAppends = {
      ...this.staticConversationAppends,
      [conversationId]: [
        ...currentMessages,
        { role: 'user', content: text },
        {
          role: 'assistant',
          content: () =>
            msg(
              'Got it. For now this is a mock reply. The real assistant will respond here.'
            ),
        },
      ],
    };
    this.clearInput();
    this.scrollMessagesToBottom();
  }

  private async sendLiveMessage(
    conversationId: string,
    text: string
  ): Promise<void> {
    this.isSending = true;
    this.errorMessage = null;
    const conversation = this.ensureConversation(conversationId);
    this.addUserMessage(conversation, text);
    const assistantMessageId = `streaming-${Date.now()}`;
    this.pendingAssistantConversationId = conversationId;
    this.scrollMessagesToBottom();
    try {
      const res = await this.sendStreamRequest(conversationId, text);
      await this.processSseStream(res, conversationId, assistantMessageId);
      this.clearInput();
      this.scrollMessagesToBottom();
    } catch (error) {
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      console.error('Failed to send chat message', error);
      this.errorMessage =
        error instanceof Error
          ? error.message || 'Failed to send your message.'
          : 'Failed to send your message.';
    } finally {
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      this.isSending = false;
      this.scheduleComposerAdjust(true);
    }
  }

  private async sendStreamRequest(
    conversationId: string,
    text: string
  ): Promise<Response> {
    const formData = new FormData();
    formData.append('content', text);
    for (const attachment of this.pendingAttachments) {
      formData.append('files', attachment.file, attachment.file.name);
    }
    formData.append('model', this.activeModel);
    if (this.activeCustomModelId) {
      formData.append('customModelId', this.activeCustomModelId);
    }

    const streamBase = getStreamApiUrl().replace(/\/$/, '');
    const streamUrl = `${streamBase}/chat/conversations/${conversationId}/messages/stream`;
    const isCrossOriginStream =
      streamBase !== getBaseApiUrl().replace(/\/$/, '');
    const headers: Record<string, string> = {};
    if (isCrossOriginStream) {
      const tokenRes = await fetch(
        `${getBaseApiUrl().replace(/\/$/, '')}/auth/stream-token`,
        {
          credentials: 'include',
        }
      );
      if (!tokenRes.ok) {
        const body = await tokenRes.text();
        throw new Error(body?.trim() || 'Failed to get stream token');
      }
      const { token } = (await tokenRes.json()) as { token: string };
      headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(streamUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const body = await res.text();
      const message = body.trim() || `Request failed (${res.status})`;
      throw new Error(message);
    }

    return res;
  }

  private ensureConversation(conversationId: string): Conversation {
    let conversation = this.dbConversations.find(
      (c) => c.id === conversationId
    );
    if (!conversation) {
      conversation = {
        id: conversationId,
        title: msg('New chat'),
        source: 'database',
        toolSelection: {
          ...EMPTY_CHAT_TOOL_SELECTION,
          mcpServerIds: [],
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [],
      };
      this.dbConversations = [conversation, ...this.dbConversations];
    }
    return conversation;
  }

  private addUserMessage(conversation: Conversation, text: string): void {
    const attachments: ConversationAttachment[] = this.pendingAttachments.map(
      (attachment) => ({
        name: attachment.file.name,
        status: 'pending',
        mimeType: attachment.file.type || undefined,
      })
    );

    const userMessage: ConversationMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
      attachments: attachments.length ? attachments : undefined,
    };
    conversation.messages = [...conversation.messages, userMessage];
    this.updateDbConversation(conversation);
  }

  private createAssistantMessage(
    conversation: Conversation,
    messageId: number | string,
    content = ''
  ): ConversationMessage {
    const assistantMessage: ConversationMessage = {
      id: messageId,
      role: 'assistant',
      content,
      createdAt: new Date().toISOString(),
    };
    conversation.messages = [...conversation.messages, assistantMessage];
    return assistantMessage;
  }

  private findMessage(
    conversation: Conversation,
    messageId: number | string
  ): ConversationMessage | undefined {
    return conversation.messages.find((message) => message.id === messageId);
  }

  private upsertToolCallTrace(
    message: ConversationMessage,
    trace: ChatToolCallTrace
  ): void {
    message.toolCalls = upsertToolCallTrace(message.toolCalls, trace);
  }

  private async processSseStream(
    res: Response,
    conversationId: string,
    assistantMessageId: string
  ): Promise<void> {
    const reader = res.body?.getReader();
    if (!reader) {
      throw new Error('Stream not available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let currentMessageId = assistantMessageId;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          try {
            const parsed = JSON.parse(data);
            currentMessageId = this.handleSseEvent(
              currentEvent,
              parsed,
              conversationId,
              currentMessageId,
              assistantMessageId
            );
          } catch {
            // Ignore parse errors for incomplete chunks
          }
          currentEvent = '';
        }

        if (line.trim() === '') {
          currentEvent = '';
        }
      }
    }
  }

  private handleSseEvent(
    event: string,
    data: unknown,
    conversationId: string,
    currentMessageId: string,
    assistantMessageId: string
  ): string {
    const parsed = data as Record<string, unknown>;
    const conversation = this.dbConversations.find(
      (c) => c.id === conversationId
    );
    if (!conversation) return currentMessageId;

    if (
      (event === 'tool_call' || event === 'tool_result') &&
      'toolCall' in parsed &&
      isToolCallTrace(parsed.toolCall)
    ) {
      let message = this.findMessage(conversation, currentMessageId);
      if (!message) {
        message = this.createAssistantMessage(conversation, currentMessageId);
      }
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      this.upsertToolCallTrace(message, parsed.toolCall);
      this.updateDbConversation(conversation);
      this.scrollMessagesToBottom();
      return currentMessageId;
    }

    if (
      event === 'chunk' &&
      'chunk' in parsed &&
      typeof parsed.chunk === 'string'
    ) {
      if (parsed.chunk.length === 0) return currentMessageId;

      let message = this.findMessage(conversation, currentMessageId);
      if (!message) {
        message = this.createAssistantMessage(conversation, currentMessageId);
      }
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      message.content += parsed.chunk;
      this.updateDbConversation(conversation);
      this.scrollMessagesToBottom();
      return currentMessageId;
    }

    if (
      event === 'done' &&
      'messageId' in parsed &&
      typeof parsed.messageId === 'string'
    ) {
      let message = this.findMessage(conversation, assistantMessageId);
      if (message) {
        message.id = parsed.messageId;
        if ('content' in parsed && typeof parsed.content === 'string') {
          message.content = parsed.content || message.content;
        }
        if ('model' in parsed && typeof parsed.model === 'string') {
          message.model = parsed.model;
        }
        if ('toolCalls' in parsed && Array.isArray(parsed.toolCalls)) {
          message.toolCalls = parsed.toolCalls.filter((toolCall) =>
            isToolCallTrace(toolCall)
          );
        }
        this.updateDbConversation(conversation);
      } else if ('content' in parsed && typeof parsed.content === 'string') {
        message = this.createAssistantMessage(
          conversation,
          parsed.messageId,
          parsed.content
        );
        if ('model' in parsed && typeof parsed.model === 'string') {
          message.model = parsed.model;
        }
        if ('toolCalls' in parsed && Array.isArray(parsed.toolCalls)) {
          message.toolCalls = parsed.toolCalls.filter((toolCall) =>
            isToolCallTrace(toolCall)
          );
        }
        this.updateDbConversation(conversation);
      }
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      return parsed.messageId;
    }

    if (
      event === 'error' &&
      'message' in parsed &&
      typeof parsed.message === 'string'
    ) {
      if (this.pendingAssistantConversationId === conversationId) {
        this.pendingAssistantConversationId = null;
      }
      this.errorMessage = parsed.message;
    }

    return currentMessageId;
  }

  private onModelChange(
    selectedModel: string,
    isCustom: boolean = false
  ): void {
    if (isCustom) {
      const custom = this.chatModelsConfig?.customModels?.find(
        (c) => c.id === selectedModel
      );
      if (!custom) return;
      this.activeCustomModelId = selectedModel;
      this.activeModel = custom.baseModel;
    } else {
      if (!this.chatModelsConfig?.models.some((m) => m.id === selectedModel))
        return;
      this.activeCustomModelId = null;
      this.activeModel = selectedModel;
    }
  }

  private onModelItemClick(
    selectedModel: string,
    isCustom: boolean,
    event: Event
  ): void {
    this.onModelChange(selectedModel, isCustom);
    this.closeOverlayFromEvent(event);
  }

  private renderModelChip(disabled: boolean) {
    return html`
      <overlay-trigger placement="top-start" type="auto" triggered-by="click">
        <button
          slot="trigger"
          class="composer-chip"
          type="button"
          title=${msg('Change model')}
          ?disabled=${disabled}
        >
          <span class="composer-chip-label">${this.activeModelLabel}</span>
          <sp-icon-chevron-down></sp-icon-chevron-down>
        </button>
        <sp-popover class="composer-menu" slot="click-content">
          <sp-menu
            label=${msg('Chat model')}
            .value=${this.activeCustomModelId ?? this.activeModel}
            .selected=${[this.activeCustomModelId ?? this.activeModel]}
          >
            ${(this.chatModelsConfig?.models ?? []).map(
              (option) => html`<sp-menu-item
                .value=${option.id}
                ?disabled=${disabled}
                ?selected=${this.isModelOptionSelected(option)}
                @click=${(event: Event) =>
                  this.onModelItemClick(option.id, false, event)}
              >
                ${this.localizeModelLabel(option.label)}
              </sp-menu-item>`
            )}
            ${
              (this.chatModelsConfig?.customModels ?? []).length
                ? html`<sp-menu-item disabled class="menu-section-label">
                    ${msg('Custom models')}
                  </sp-menu-item>`
                : nothing
            }
            ${(this.chatModelsConfig?.customModels ?? []).map(
              (option) => html`<sp-menu-item
                .value=${option.id}
                ?disabled=${disabled}
                ?selected=${this.activeCustomModelId === option.id}
                @click=${(event: Event) =>
                  this.onModelItemClick(option.id, true, event)}
              >
                ${this.getCustomModelOptionLabel(option)}
              </sp-menu-item>`
            )}
            <sp-menu-item
              class="manage-models-menu-item"
              ?disabled=${disabled}
              @click=${(event: Event) => this.openCustomModelsView(event)}
            >
              ${msg('Manage custom models')}
            </sp-menu-item>
          </sp-menu>
        </sp-popover>
      </overlay-trigger>
    `;
  }

  private renderToolsChip(disabled: boolean) {
    if (this.selectedConversation?.source !== 'database') {
      return nothing;
    }

    const selection = this.activeToolSelection;
    const itemDisabled = disabled || this.isUpdatingToolSelection;

    return html`
      <overlay-trigger placement="top-start" type="auto" triggered-by="click">
        <button
          slot="trigger"
          class="composer-chip"
          type="button"
          title=${msg('Choose tools')}
          ?disabled=${itemDisabled}
        >
          <span class="composer-chip-label">${this.toolsLabel}</span>
          <sp-icon-chevron-down></sp-icon-chevron-down>
        </button>
        <sp-popover class="composer-menu" slot="click-content">
          <sp-menu label=${msg('Tools')}>
            <sp-menu-item disabled class="menu-section-label">
              ${msg('Knowledge')}
            </sp-menu-item>
            <sp-menu-item
              ?selected=${selection.taxKnowledgeEnabled}
              ?disabled=${itemDisabled}
              @click=${() => this.toggleKnowledgeTool('taxKnowledgeEnabled')}
            >
              ${msg('Tax knowledge')}
            </sp-menu-item>
            <sp-menu-item
              ?selected=${selection.datevKnowledgeEnabled}
              ?disabled=${itemDisabled}
              @click=${() => this.toggleKnowledgeTool('datevKnowledgeEnabled')}
            >
              ${msg('DATEV knowledge')}
            </sp-menu-item>
            ${
              this.mcpServers.length
                ? html`
                    <sp-menu-item disabled class="menu-section-label">
                      ${msg('MCP servers')}
                    </sp-menu-item>
                    ${this.mcpServers.map(
                      (server) => html`<sp-menu-item
                        ?selected=${selection.mcpServerIds.includes(server.id)}
                        ?disabled=${itemDisabled}
                        @click=${() => this.toggleMcpServer(server.id)}
                      >
                        ${server.name}
                      </sp-menu-item>`
                    )}
                  `
                : nothing
            }
          </sp-menu>
        </sp-popover>
      </overlay-trigger>
    `;
  }

  private onInput(event: InputEvent): void {
    const el = event.target as HTMLTextAreaElement;
    this.inputText = el.value;
    this.errorMessage = null;
    this.resizeTextarea();
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSending) {
        void this.onSend();
      }
    }
  }

  private resizeTextarea(): void {
    const textarea = this.renderRoot?.querySelector(
      '.composer textarea'
    ) as HTMLTextAreaElement | null;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const max = 150;
    const next = Math.max(Math.min(max, textarea.scrollHeight), 48);
    textarea.style.height = `${next}px`;
  }

  private focusComposer(): void {
    const textarea = this.renderRoot?.querySelector(
      '.composer textarea'
    ) as HTMLTextAreaElement | null;
    textarea?.focus();
  }

  private scheduleComposerAdjust(focus = false): void {
    requestAnimationFrame(() => {
      this.resizeTextarea();
      this.updateInputReserve();
      if (focus) this.focusComposer();
    });
  }

  private updateInputReserve(): void {
    const input = this.renderRoot?.querySelector(
      '.input'
    ) as HTMLElement | null;
    if (!input) {
      this.style.setProperty('--mobile-input-reserve', '0px');
      return;
    }
    this.style.setProperty('--mobile-input-reserve', `${input.offsetHeight}px`);
  }

  private scrollMessagesToBottom(): void {
    requestAnimationFrame(() => {
      const scroller = this.renderRoot?.querySelector(
        '.messages'
      ) as HTMLElement | null;
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    });
  }

  private clearInput(): void {
    this.inputText = '';
    this.pendingAttachments = [];
    this.dragDepth = 0;
    this.isDragActive = false;
    this.scheduleComposerAdjust(true);
  }

  private renderCustomModelsView() {
    return html`
      <div class="models-workspace">
        <div class="models-workspace-body">
          <kv-module-custom-models
            embedded
            @custom-models-close=${() => this.closeCustomModelsView(true)}
            @custom-models-changed=${this.onCustomModelsChanged}
          ></kv-module-custom-models>
        </div>
      </div>
    `;
  }

  render() {
    const conversation = this.selectedConversation;
    const messages = conversation?.messages ?? [];
    const visibleMessages = messages.filter(
      (message) => message.role !== 'system'
    );
    const showPendingAssistantPlaceholder =
      conversation?.id === this.pendingAssistantConversationId;
    const showCenteredEmptyState = !conversation;
    const composerDisabled =
      this.isSending ||
      this.isUpdatingToolSelection ||
      this.audioStatus === 'sending' ||
      this.audioStatus === 'recording' ||
      !conversation;
    const micDisabled =
      !conversation ||
      conversation.source !== 'database' ||
      this.isSending ||
      this.isUpdatingToolSelection ||
      this.audioStatus === 'sending';
    const audioStatusLabel =
      this.audioStatus === 'recording'
        ? msg('Recording...')
        : this.audioStatus === 'sending'
          ? msg('Sending...')
          : this.audioStatus === 'speaking'
            ? msg('Speaking...')
            : null;

    const chatAreaClasses = {
      'chat-area': true,
      'drag-active': this.isDragActive,
      'models-open': this.activeView === 'models',
    };

    // Mobile sidebar state is applied on :host via classList (see updated()).

    const composerClasses = {
      composer: true,
      dragging: this.isDragActive,
    };

    return html`
      <div
        class="backdrop"
        role="button"
        tabindex="0"
        aria-label=${msg('Close history')}
        @click=${() => {
          this.requestCloseMobileNavigation();
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ')
            this.requestCloseMobileNavigation();
        }}
      ></div>
      ${
        this.confirmDeleteConversationId
          ? html`
              <kv-confirmation-dialog
                heading=${msg('Delete chat?')}
                confirm-label=${msg('Delete')}
                .confirming=${this.isDeletingConversation}
                @cancel=${() => this.cancelDeleteConversation()}
                @confirm=${() => void this.confirmDeleteConversation()}
              >
                <p>
                  ${msg('This chat will be removed from your chat history view.')}
                </p>
              </kv-confirmation-dialog>
            `
          : null
      }
      <sp-split-view
        class="chat-split"
        resizable
        primary-size="288px"
        .primaryMin=${220}
        .primaryMax=${480}
        label=${msg('Resize chat sidebar')}
      >
      <div class="conversations">
        <div class="new-chat">
          <button
            class="new-chat-button"
            type="button"
            @click=${() => {
              this.requestCloseMobileNavigation();
              void this.startNewConversation();
              this.focusComposer();
            }}
            ?disabled=${this.isCreating || this.isSending}
          >
            <sp-icon-add></sp-icon-add>
            ${msg('Start new chat')}
          </button>
        </div>
        <ul class="conversation-list" role="list">
          ${this.allConversations.map(
            (conv) => html`<li class="conversation-row">
              <button
                type="button"
                class=${classMap({
                  'conversation-item': true,
                  'is-selected': conv.id === this.selectedId,
                })}
                aria-current=${conv.id === this.selectedId ? 'true' : 'false'}
                title=${conv.title}
                @click=${() => this.selectConversation(conv.id)}
              >
                <span class="conversation-content">
                  <span class="conversation-item-title">${conv.title}</span>
                  <span class="conversation-item-meta">
                    ${this.getConversationMeta(conv)}
                  </span>
                </span>
              </button>
                ${
                  conv.source === 'database'
                    ? html`<sp-action-button
                        class="conversation-delete"
                        quiet
                        size="s"
                        title=${msg('Delete chat')}
                        aria-label=${msg(str`Delete chat ${conv.title}`)}
                        ?disabled=${this.isDeletingConversation}
                        @click=${(event: Event) =>
                          this.promptDeleteConversation(conv.id, event)}
                      >
                        <sp-icon-delete slot="icon"></sp-icon-delete>
                      </sp-action-button>`
                    : null
                }
            </li>`
          )}
        </ul>
      </div>
      <div
        class=${classMap(chatAreaClasses)}
        @dragenter=${(event: DragEvent) => this.onDragEnter(event)}
        @dragover=${(event: DragEvent) => this.onDragOver(event)}
        @dragleave=${(event: DragEvent) => this.onDragLeave(event)}
        @drop=${(event: DragEvent) => this.onDrop(event)}
      >
        ${
          this.activeView === 'models'
            ? this.renderCustomModelsView()
            : html`
        ${
          this.errorMessage
            ? html`<div class="status">${this.errorMessage}</div>`
            : null
        }
        <div class="messages ${showCenteredEmptyState ? 'empty' : ''}">
          ${
            !conversation
              ? html`<div class="empty-state">
                  <p class="empty-state-title">${msg('Start a new chat')}</p>
                  <p class="empty-state-text">
                    ${msg(
                      'Ask Klarvos about a client, workflow, document, or DATEV task.'
                    )}
                  </p>
                </div>`
              : visibleMessages.length === 0
                ? html`
                  <div class="conversation-title">${conversation.title}</div>
                  <div class="empty-state">
                    <p class="empty-state-title">${msg('No messages yet')}</p>
                    <p class="empty-state-text">
                      ${msg('Send the first message to begin this conversation.')}
                    </p>
                  </div>
                `
                : html`
                  <div class="conversation-title">${conversation.title}</div>
                  ${visibleMessages.map((message) => {
                    if (message.role === 'assistant') {
                      return html`<kv-chat-assistant-message
                        .message=${{ ...message }}
                      ></kv-chat-assistant-message>`;
                    }
                    return html`<kv-chat-user-message
                      .message=${message}
                    ></kv-chat-user-message>`;
                  })}
                  ${
                    showPendingAssistantPlaceholder
                      ? html`<kv-chat-assistant-message
                          .message=${{ role: 'assistant', content: '' }}
                          .thinking=${true}
                        ></kv-chat-assistant-message>`
                      : nothing
                  }
                `
          }
        </div>
        <div class="input">
          <div class=${classMap(composerClasses)}>
            ${
              this.pendingAttachments.length
                ? html`<div class="attachment-list">
                  ${this.pendingAttachments.map(
                    (attachment) => html`<div class="attachment-chip">
                      <sp-icon-attach></sp-icon-attach>
                      <span>${attachment.file.name}</span>
                      <button
                        class="remove"
                        type="button"
                        @click=${() => this.removeAttachment(attachment.id)}
                        aria-label=${msg(str`Remove ${attachment.file.name}`)}
                      >
                        &times;
                      </button>
                    </div>`
                  )}
                </div>`
                : null
            }
            <textarea
              placeholder=${msg('Ask anything')}
              .value=${this.inputText}
              ?disabled=${composerDisabled}
              @input=${(event: InputEvent) => this.onInput(event)}
              @keydown=${(event: KeyboardEvent) => this.onKeydown(event)}
            ></textarea>
            <div class="composer-toolbar">
              <div class="composer-context">
                ${this.renderModelChip(composerDisabled)}
                ${this.renderToolsChip(composerDisabled)}
              </div>
              <div class="composer-actions">
                <sp-action-button
                  quiet
                  title=${msg('Attach files')}
                  ?disabled=${composerDisabled}
                  @click=${() => this.openFilePicker()}
                >
                  <sp-icon-attach slot="icon"></sp-icon-attach>
                </sp-action-button>
              <sp-action-button
                quiet
                title=${
                  this.audioStatus === 'recording'
                    ? msg('Stop recording')
                    : msg('Record voice message')
                }
                ?disabled=${micDisabled}
                @click=${() => this.onMicClick()}
              >
                <sp-icon-microphone slot="icon"></sp-icon-microphone>
              </sp-action-button>
              <sp-action-button
                class="send-button"
                quiet
                title=${msg('Send')}
                ?disabled=${!this.inputText.trim() || this.isSending || !conversation}
                @click=${() => void this.onSend()}
              >
                <sp-icon-send slot="icon"></sp-icon-send>
              </sp-action-button>
              </div>
            </div>
            <input
              id="chat-attachment-input"
              type="file"
              multiple
              hidden
              ?disabled=${composerDisabled}
              @change=${(event: Event) => this.onFileInputChange(event)}
            />
          </div>
          ${audioStatusLabel ? html`<div class="audio-status">${audioStatusLabel}</div>` : null}
        </div>
            `
        }
      </div>
      </sp-split-view>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-chat': KvModuleChat;
  }
}
